import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { includes } from 'lodash';
import { configurations as i18nNamespace } from '../../../../i18n/namespace';
import { history } from 'store';
import { isAllowed, isOrgAdmin } from 'utils/permissions';
import ConfigurationInfoCard from './ConfigurationInfoCard';
import { ReactComponent as InternetAccessIcon } from '../../assets/icon-internet-access-2color.svg';
import { ReactComponent as AutoSupportIcon } from '../../assets/icon-autosupport-2color.svg';
import { ReactComponent as SyslogIcon } from '../../assets/icon-syslog-2color.svg';
import { ReactComponent as SiemIcon } from '../../assets/icon-siem-2color.svg';
import { ReactComponent as NetworkAliasesIcon } from '../../assets/icon-network-aliases-2color.svg';

export { default as SecurityCertificateCard } from './SecurityCertificateCard';

// The cards below the certificate one have no backend yet, so they stay behind
// this flag until their features land.
export const SYSTEM_CONFIGURATION_FEATURE = 'NetApp.Console/system-configuration';

const createCard = (displayName, { titleKey, descriptionKey, Icon, routePath, requiresOrgAdmin = false }) => {
    const Card = () => {
        const { t } = useTranslation(i18nNamespace);
        const { selectedOrg: { rules } = {} } = useSelector(state => state.tenancy);
        const canViewCard = isAllowed(rules, 'notifications', 'access');
        const canConfigure = requiresOrgAdmin
            ? isOrgAdmin(rules)
            : includes(rules, 'bxp:notifications-create');

        return (
            <ConfigurationInfoCard
                title={t(titleKey)}
                description={t(descriptionKey)}
                iconRenderer={() => <Icon />}
                actionLabel={t('configure')}
                onAction={() => history.push(routePath)}
                hasPermission={canConfigure}
                hasNoPermission={!canViewCard}
            />
        );
    };
    Card.displayName = displayName;

    return React.memo(Card);
};

export const InternetAccessCard = createCard('InternetAccessCard', {
    titleKey: 'internet_access',
    descriptionKey: 'internet_access_desc',
    Icon: InternetAccessIcon,
    routePath: '/console-configurations/internet-access',
    requiresOrgAdmin: true,
});

export const AutoSupportCard = createCard('AutoSupportCard', {
    titleKey: 'autosupport_messages',
    descriptionKey: 'autosupport_messages_desc',
    Icon: AutoSupportIcon,
    routePath: '/console-configurations/autosupport',
    requiresOrgAdmin: true,
});

export const SyslogCard = createCard('SyslogCard', {
    titleKey: 'syslog',
    descriptionKey: 'syslog_desc',
    Icon: SyslogIcon,
    routePath: '/console-configurations/syslog',
});

export const SiemIntegrationCard = createCard('SiemIntegrationCard', {
    titleKey: 'siem_integration',
    descriptionKey: 'siem_integration_desc',
    Icon: SiemIcon,
    routePath: '/console-configurations/siem',
});

export const NetworkAliasesCard = createCard('NetworkAliasesCard', {
    titleKey: 'network_aliases',
    descriptionKey: 'network_aliases_desc',
    Icon: NetworkAliasesIcon,
    routePath: '/console-configurations/network-aliases',
});
